import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import {
    IonRadioGroup,
    IonRadio,
    IonItem,
    IonLabel,
    IonList,
    IonModal,
    IonContent,
} from "@ionic/react";


// Union type for all question types
type Question =
    | MultipleChoiceQuestion
    | FillInTheBlankQuestion
    | TrueFalseQuestion

// Question Type Definitions
interface BaseQuestion {
    question: string;
}

interface MultipleChoiceQuestion extends BaseQuestion {
    type: "multiple-choice";
    options: string[];
    answer: string;
}

interface FillInTheBlankQuestion extends BaseQuestion {
    type: "fill-in-the-blank";
    options: string[];
    answer: string;
}

interface TrueFalseQuestion extends BaseQuestion {
    type: "true-false";
    answer: string;
}




const AnimalBirds: React.FC = () => {
    const [questions, setQuestions] = useState<Question[]>([]);
    const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [matchOption, setMatchOption] = useState<string[] | null>(null);
    const [showImage, setShowImage] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const modal = useRef<HTMLIonModalElement>(null);

    const loadRandomSyllabusQuestions = async () => {

        try {
            let data: Question[] = [];
            if (process.env.REACT_APP_DEVELOPMENT !== "WEB") {
                const response = await fetch(
                    process.env.REACT_APP_API_URL + "ClassThreeScience/AnimalBirds.json"
                );
                data = await response.json();
            } else {
                const module = await import(
                    `../../../datas/ClassThreeScience/AnimalBirds.json`
                );
                data = module.default as Question[];
            }

            setQuestions(data);
            const shuffledQuestions = shuffleArray(data);
            setQuestions(shuffledQuestions);
            setCurrentQuestion(shuffledQuestions[currentIndex]);
        } catch (err) {
            console.error("Failed to load questions:", err);
        }
    };

    const handleAnswerSubmit = (option: string) => {
        setSelectedOption(option);
        let isCorrect;
        if (!currentQuestion) return;

        let correctAnswer: string | boolean = "";
        if (
            currentQuestion.type === "multiple-choice" ||
            currentQuestion.type === "fill-in-the-blank"
        ) {
            correctAnswer = currentQuestion.answer;

            isCorrect = option.trim().toLowerCase() === correctAnswer.trim().toLowerCase()

        } else if (currentQuestion.type === "true-false") {
            correctAnswer = currentQuestion.answer;
            isCorrect = option.trim().toLowerCase() === String(correctAnswer).toLowerCase()
        }

        if (isCorrect == true) {
            setShowImage(true);
            setTimeout(() => {
                const nextIndex = currentIndex + 1;
                if (nextIndex < questions.length) {
                    setCurrentIndex(nextIndex);
                    setShowImage(false);
                    setSelectedOption(null);
                    setCurrentQuestion(questions[nextIndex]);
                }
                else {
                    setCurrentIndex(0);
                    loadRandomSyllabusQuestions();
                }
            }, 5000);
        }
    };

    // Function to shuffle an array
    function shuffleArray(array: any[]) {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    }

    useEffect(() => {
        loadRandomSyllabusQuestions();

        document.title =
            "Animals: Birds – Class 3 Science | Mama’s Guidance";
    }, []);

    return (
        <>
            <Helmet>
                <meta name="description" content="Discover the world of Animals and Birds in Class 3 Science. Learn about their features, movements, food habits, and homes with easy explanations at Mama’s Guidance." />
            </Helmet>
            {!showImage ? (
                <div className="contentPart showCenter additionWord">
                    <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                    <IonModal
                        ref={modal}
                        trigger="open-modal"
                        initialBreakpoint={1}
                        breakpoints={[0, 0.5, 0.75, 1]}
                        handleBehavior="cycle"
                        backdropDismiss={false}  // ✅ Prevents click-to-close
                        className="full-page-modal"
                    >
                        <IonContent className="ion-padding">
                            <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                            <h1 className="mb-5">Chapter 3: Birds</h1>

                            <section>
                                <h2><span className="emoji">🦜</span>&nbsp;Types of Beaks, Claws & Feathers</h2>

                                <h3>🪶 Bird Beaks – Built for Eating</h3>
                                <ul>
                                    <li><strong>Hooked beaks</strong> – Eagles, owls (tearing flesh)</li>
                                    <li><strong>Long, slender beaks</strong> – Hummingbirds (sipping nectar)</li>
                                    <li><strong>Short, hard beaks</strong> – Sparrows, pigeons (cracking seeds)</li>
                                    <li><strong>Flat, broad beaks</strong> – Ducks (filtering food from water)</li>
                                </ul>

                                <h3>🦤 Claws – Tools for Survival</h3>
                                <ul>
                                    <li><strong>Sharp talons</strong> – Eagles, hawks (catching animals)</li>
                                    <li><strong>Webbed feet</strong> – Ducks, swans (swimming)</li>
                                    <li><strong>Scratching claws</strong> – Hens (digging soil)</li>
                                    <li><strong>Climbing claws</strong> – Woodpeckers (gripping trees)</li>
                                </ul>

                                <h3>🪶 Feathers – Light and Useful</h3>
                                <ul>
                                    <li><strong>Flight feathers</strong> – Help birds fly</li>
                                    <li><strong>Down feathers</strong> – Keep the bird warm</li>
                                    <li><strong>Body feathers</strong> – Give color and shape</li>
                                </ul>
                            </section>

                            <section>
                                <h2><span className="emoji">🍽️</span>&nbsp;Food Habits in Birds</h2>
                                <p>Birds eat different types of food based on their beaks and environment:</p>
                                <ul>
                                    <li><strong>Carnivores</strong> – Hawks, owls (eat meat)</li>
                                    <li><strong>Seed-eaters</strong> – Sparrows, pigeons</li>
                                    <li><strong>Nectar-feeders</strong> – Hummingbirds, sunbirds</li>
                                    <li><strong>Omnivores</strong> – Crows (eat both plants and animals)</li>
                                </ul>
                            </section>

                            <section>
                                <h2><span className="emoji">🪺</span>&nbsp;Nesting Habits in Birds</h2>
                                <p>Birds build nests to lay eggs and raise chicks. Nest types include:</p>
                                <ul>
                                    <li><strong>Weaver birds</strong> – Weave hanging nests from grass</li>
                                    <li><strong>Tailor birds</strong> – Stitch leaves with their beaks</li>
                                    <li><strong>Woodpeckers</strong> – Dig holes in trees</li>
                                    <li><strong>Sparrows and pigeons</strong> – Build simple nests on buildings</li>
                                    <li><strong>Cuckoos</strong> – Lay eggs in other birds&apos; nests</li>
                                </ul>
                            </section>

                            <section>
                                <h2><span className="emoji">🐣</span>&nbsp;Care of Young Birds</h2>
                                <p>Birds are caring parents. After the chicks hatch:</p>
                                <ul>
                                    <li>Parents <strong>feed the chicks</strong> by chewing food and giving it to them</li>
                                    <li>They <strong>keep the chicks warm</strong> with their feathers</li>
                                    <li>Both parents often <strong>take turns caring</strong> for their babies</li>
                                    <li>Chicks learn to <strong>fly</strong> with the help of their parents</li>
                                </ul>
                            </section>

                            <section className="summary">
                                <h2><span className="emoji">🌟</span>&nbsp;Summary</h2>
                                <ul>
                                    <li>Birds have different types of beaks, claws, and feathers for different tasks.</li>
                                    <li>Their food habits depend on the shape of their beaks.</li>
                                    <li>Birds are clever nest-builders and choose safe spots.</li>
                                    <li>They take good care of their chicks until they can fly.</li>
                                </ul>
                            </section>

                            <section className="activity">
                                <h2><span className="emoji">📚</span>&nbsp;Fun Activity</h2>
                                <ul>
                                    <li>🎨 Draw and label the beaks of 3 different birds.</li>
                                    <li>🔍 Observe birds around your home. Try to guess their food habits.</li>
                                    <li>🧺 Build a model nest using grass, leaves, and twigs.</li>
                                </ul>
                            </section>
                            <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                        </IonContent>
                    </IonModal>
                    <article className="visually-hidden">
                        <h1>🐦 Class 3 Science – Chapter 3: Birds</h1>

                        <section>
                            <h2><span className="emoji">🦜</span>&nbsp;Types of Beaks, Claws & Feathers</h2>

                            <h3>🪶 Bird Beaks – Built for Eating</h3>
                            <ul>
                                <li><strong>Hooked beaks</strong> – Eagles, owls (tearing flesh)</li>
                                <li><strong>Long, slender beaks</strong> – Hummingbirds (sipping nectar)</li>
                                <li><strong>Short, hard beaks</strong> – Sparrows, pigeons (cracking seeds)</li>
                                <li><strong>Flat, broad beaks</strong> – Ducks (filtering food from water)</li>
                            </ul>

                            <h3>🦤 Claws – Tools for Survival</h3>
                            <ul>
                                <li><strong>Sharp talons</strong> – Eagles, hawks (catching animals)</li>
                                <li><strong>Webbed feet</strong> – Ducks, swans (swimming)</li>
                                <li><strong>Scratching claws</strong> – Hens (digging soil)</li>
                                <li><strong>Climbing claws</strong> – Woodpeckers (gripping trees)</li>
                            </ul>

                            <h3>🪶 Feathers – Light and Useful</h3>
                            <ul>
                                <li><strong>Flight feathers</strong> – Help birds fly</li>
                                <li><strong>Down feathers</strong> – Keep the bird warm</li>
                                <li><strong>Body feathers</strong> – Give color and shape</li>
                            </ul>
                        </section>

                        <section>
                            <h2><span className="emoji">🍽️</span>&nbsp;Food Habits in Birds</h2>
                            <p>Birds eat different types of food based on their beaks and environment:</p>
                            <ul>
                                <li><strong>Carnivores</strong> – Hawks, owls (eat meat)</li>
                                <li><strong>Seed-eaters</strong> – Sparrows, pigeons</li>
                                <li><strong>Nectar-feeders</strong> – Hummingbirds, sunbirds</li>
                                <li><strong>Omnivores</strong> – Crows (eat both plants and animals)</li>
                            </ul>
                        </section>

                        <section>
                            <h2><span className="emoji">🪺</span>&nbsp;Nesting Habits in Birds</h2>
                            <p>Birds build nests to lay eggs and raise chicks. Nest types include:</p>
                            <ul>
                                <li><strong>Weaver birds</strong> – Weave hanging nests from grass</li>
                                <li><strong>Tailor birds</strong> – Stitch leaves with their beaks</li>
                                <li><strong>Woodpeckers</strong> – Dig holes in trees</li>
                                <li><strong>Sparrows and pigeons</strong> – Build simple nests on buildings</li>
                                <li><strong>Cuckoos</strong> – Lay eggs in other birds&apos; nests</li>
                            </ul>
                        </section>

                        <section>
                            <h2><span className="emoji">🐣</span>&nbsp;Care of Young Birds</h2>
                            <p>Birds are caring parents. After the chicks hatch:</p>
                            <ul>
                                <li>Parents <strong>feed the chicks</strong> by chewing food and giving it to them</li>
                                <li>They <strong>keep the chicks warm</strong> with their feathers</li>
                                <li>Both parents often <strong>take turns caring</strong> for their babies</li>
                                <li>Chicks learn to <strong>fly</strong> with the help of their parents</li>
                            </ul>
                        </section>

                        <section className="summary">
                            <h2><span className="emoji">🌟</span>&nbsp;Summary</h2>
                            <ul>
                                <li>Birds have different types of beaks, claws, and feathers for different tasks.</li>
                                <li>Their food habits depend on the shape of their beaks.</li>
                                <li>Birds are clever nest-builders and choose safe spots.</li>
                                <li>They take good care of their chicks until they can fly.</li>
                            </ul>
                        </section>

                        <section className="activity">
                            <h2><span className="emoji">📚</span>&nbsp;Fun Activity</h2>
                            <ul>
                                <li>🎨 Draw and label the beaks of 3 different birds.</li>
                                <li>🔍 Observe birds around your home. Try to guess their food habits.</li>
                                <li>🧺 Build a model nest using grass, leaves, and twigs.</li>
                            </ul>
                        </section>
                    </article>
                    {currentQuestion ? (
                        <>
                            <h3 className="mb-3">{currentQuestion.question}</h3>
                            {(currentQuestion.type === "multiple-choice" ||
                                currentQuestion.type === "fill-in-the-blank") && (
                                    <IonRadioGroup
                                        onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                    >
                                        <IonList>
                                            {(currentQuestion.options || []).map((option, index) => (
                                                <IonItem
                                                    className={`grid-item ${selectedOption === option
                                                        ? option.toLowerCase() === currentQuestion.answer.toLowerCase()
                                                            ? "correct"
                                                            : "errorItems"
                                                        : ""
                                                        }`}
                                                    key={index}
                                                >
                                                    <IonRadio slot="start" value={option} />
                                                    <IonLabel>{option}</IonLabel>
                                                </IonItem>
                                            ))}
                                        </IonList>
                                    </IonRadioGroup>
                                )}
                            {currentQuestion.type === "true-false" && (
                                <IonRadioGroup
                                    onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                >
                                    <IonList>
                                        {["true", "false"].map((option, index) => (
                                            <IonItem key={index} className={`${selectedOption === option
                                                ? option.toLowerCase() === String(currentQuestion.answer).toLowerCase()
                                                    ? "correct"
                                                    : "errorItems"
                                                : ""
                                                }`}>
                                                <IonRadio slot="start" value={option} />
                                                <IonLabel>{option.toUpperCase()}</IonLabel>
                                            </IonItem>
                                        ))}
                                    </IonList>
                                </IonRadioGroup>
                            )}
                        </>
                    ) : (
                        <p>Loading question...</p>
                    )}
                </div>
            ) : (
                <div className="contentPart showMiddle">
                    <img
                        style={{ height: "150px" }}
                        className="img-fluid"
                        src={process.env.REACT_APP_WELDONE_LINK}
                        alt="Well done"
                    />
                </div>
            )}
        </>
    );
};

export default AnimalBirds;
